import { ShoppingBag, Star, Diamond, Gift, Sparkles, Loader2, Check, ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { shopAPI } from '@/api/shop';
import { usePoints } from '@/context/PointsContext';
import { toast } from 'sonner';

const ITEM_TYPE_LABELS = {
  'CHARACTER': 'Budlee AI Characters',
  'AVATAR': 'Avatars',
  'FRAME': 'Profile Frames',
  'BOOSTER': 'Boosters',
  'PROTECTOR': 'Protectors',
};

export default function ShopPage() {
  const { points, refreshPoints } = usePoints();
  const [shopItems, setShopItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [equipping, setEquipping] = useState(null);
  const [error, setError] = useState(null);

  // Fetch shop items and inventory
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch shop items - this is required
        const itemsResponse = await shopAPI.getItems({ is_active: true });
        const items = Array.isArray(itemsResponse) ? itemsResponse : (itemsResponse.results || itemsResponse);
        setShopItems(items);
        
        // Try to fetch inventory, but don't fail if it errors
        try {
          const inventoryResponse = await shopAPI.getInventory();
          const inventoryItems = Array.isArray(inventoryResponse) ? inventoryResponse : (inventoryResponse.results || inventoryResponse);
          setInventory(inventoryItems);
        } catch (inventoryErr) {
          console.warn('Error fetching inventory (non-critical):', inventoryErr);
          // Continue without inventory - items will still show
          setInventory([]);
        }
      } catch (err) {
        console.error('Error fetching shop data:', err);
        const errorMessage = err.message || 'Failed to load shop. Please try again.';
        console.error('Shop fetch error details:', {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Check if item is owned (exclude BOOSTER and PROTECTOR - they are consumable)
  const isOwned = (itemId, itemType) => {
    // BOOSTER and PROTECTOR items are always repurchasable
    if (itemType === 'BOOSTER' || itemType === 'PROTECTOR') {
      return false;
    }
    return inventory.some(inv => inv.shop_item?.id === itemId || inv.shop_item_id === itemId);
  };

  // Get inventory item for shop item
  const getInventoryItem = (itemId) => {
    return inventory.find(inv => inv.shop_item?.id === itemId || inv.shop_item_id === itemId);
  };

  // Handle purchase
  const handlePurchase = async (itemId) => {
    if (purchasing) return;
    
    setPurchasing(itemId);
    
    try {
      const result = await shopAPI.purchaseItem(itemId);
      
      // Refresh points to ensure UI is updated (gems should decrease after purchase)
      await refreshPoints();
      
      // For consumables (BOOSTER/PROTECTOR), don't refresh inventory as they're not stored
      // For other items, refresh inventory
      if (!result.is_consumable) {
        const inventoryResponse = await shopAPI.getInventory();
        const inventoryItems = Array.isArray(inventoryResponse) ? inventoryResponse : (inventoryResponse.results || inventoryResponse);
        setInventory(inventoryItems);
      }
      
      const itemName = result.shop_item?.item_name || 'item';
      const message = result.is_consumable 
        ? `${itemName} activated!` 
        : `Successfully purchased ${itemName}!`;
      toast.success(message);
    } catch (err) {
      console.error('Error purchasing item:', err);
      toast.error(err.message || 'Failed to purchase item. Please try again.');
    } finally {
      setPurchasing(null);
    }
  };

  // Handle equip/unequip
  const handleEquip = async (inventoryItemId) => {
    if (equipping) return;
    
    setEquipping(inventoryItemId);
    
    try {
      await shopAPI.equipItem(inventoryItemId);
      
      // Refresh inventory
      const inventoryResponse = await shopAPI.getInventory();
      const inventoryItems = Array.isArray(inventoryResponse) ? inventoryResponse : (inventoryResponse.results || inventoryResponse);
      setInventory(inventoryItems);
      
      // Trigger events to update sidebar and other components
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('equippedItemsUpdated'));
      
      toast.success('Item applied successfully!');
    } catch (err) {
      console.error('Error equipping item:', err);
      toast.error(err.message || 'Failed to equip item. Please try again.');
    } finally {
      setEquipping(null);
    }
  };

  // Group items by type
  const groupedItems = shopItems.reduce((acc, item) => {
    const type = item.item_type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(item);
    return acc;
  }, {});

  // Sort groups by display order
  const sortedGroups = Object.entries(groupedItems).sort((a, b) => {
    const order = ['CHARACTER', 'AVATAR', 'FRAME', 'BOOSTER', 'PROTECTOR'];
    return order.indexOf(a[0]) - order.indexOf(b[0]);
  });

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-3 sm:p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-4">
              <ShoppingBag className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--green-bright))]" />
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">Shop</h1>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Redeem your gems for awesome rewards and power-ups</p>
          </div>
          <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg">
            <Diamond className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
            <span className="text-base sm:text-xl font-bold text-foreground">{points?.gems || 0}</span>
            <span className="text-xs sm:text-sm text-muted-foreground">Gems</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="p-4">
              <p className="text-xs sm:text-sm text-red-400">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-foreground" />
          </div>
        ) : (
          <>
            {/* Shop Items by Category */}
            {sortedGroups.map(([type, items]) => {
              // Sort items: free/default items first, then by display_order
              const sortedItems = [...items].sort((a, b) => {
                if (a.is_free !== b.is_free) {
                  return a.is_free ? -1 : 1; // Free items first
                }
                return (a.display_order || 0) - (b.display_order || 0);
              });
              
              return (
                <div key={type} className="space-y-3">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                      {ITEM_TYPE_LABELS[type] || type}
                    </h2>
                    {sortedItems.length > 0 && sortedItems.every(item => item.is_free) && (
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        More items coming soon!
                      </p>
                    )}
                  </div>
                  {/* Horizontal Scrollable Container */}
                  <div className="overflow-x-auto pb-2 -mx-2 px-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent' }}>
                    <div className="flex gap-3 min-w-max">
                      {sortedItems.map((item) => {
                      const owned = isOwned(item.id, item.item_type);
                      const inventoryItem = getInventoryItem(item.id);
                      const canAfford = item.is_free || (points?.gems || 0) >= item.cost_in_gems;
                      const isDisabled = !owned && !canAfford && !item.is_free;
                      const isConsumable = item.item_type === 'BOOSTER' || item.item_type === 'PROTECTOR';
                      
                      return (
                        <Card
                          key={item.id}
                          className={`bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] transition-all flex-shrink-0 w-[160px] sm:w-[200px] ${
                            owned ? 'ring-2 ring-green-500/50' : isDisabled ? 'opacity-60' : 'hover:border-[hsl(var(--primary))]'
                          }`}
                        >
                          <CardContent className="p-3 sm:p-4 flex flex-col h-full">
                            {/* Item Image - Square Preview */}
                            <div className="w-full aspect-square bg-[hsl(var(--main-bg))] rounded-lg flex items-center justify-center overflow-hidden mb-3 flex-shrink-0">
                              {item.image_url_display || item.image ? (
                                <img
                                  src={item.image_url_display || item.image}
                                  alt={item.item_name}
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className="w-full h-full flex items-center justify-center" style={{ display: item.image_url_display || item.image ? 'none' : 'flex' }}>
                                <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
                              </div>
                            </div>
                            
                            {/* Item Info */}
                            <div className="flex flex-col mb-3">
                              <h3 className="font-bold text-foreground text-xs sm:text-base mb-1 line-clamp-1">{item.item_name}</h3>
                              {item.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
                              )}
                            </div>
                            
                            {/* Price and Actions - Fixed at Bottom */}
                            <div className="flex items-center justify-between pt-2 border-t border-[hsl(var(--card-border))] mt-auto">
                              <div className="flex items-center gap-1">
                                <Diamond className="w-3 h-3 text-purple-500" />
                                <span className="font-semibold text-foreground text-xs sm:text-sm">
                                  {item.is_free ? 'FREE' : item.cost_in_gems}
                                </span>
                              </div>
                              
                              {owned && !isConsumable ? (
                                <div className="flex items-center">
                                  {inventoryItem?.is_equipped ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-green-500 border-green-500 h-6 px-2 text-xs"
                                      disabled={equipping === inventoryItem.id}
                                      onClick={() => handleEquip(inventoryItem.id)}
                                    >
                                      {equipping === inventoryItem.id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <>
                                          <Check className="w-3 h-3 mr-1" />
                                          <span className="hidden sm:inline">Applied</span>
                                        </>
                                      )}
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      className="bg-green-500 hover:bg-green-600 h-7 px-2 text-xs"
                                      disabled={equipping === inventoryItem.id}
                                      onClick={() => handleEquip(inventoryItem.id)}
                                    >
                                      {equipping === inventoryItem.id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        'Apply'
                                      )}
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col items-end gap-0.5">
                                  <Button
                                    className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] disabled:opacity-50 disabled:cursor-not-allowed h-7 px-3 text-xs"
                                    size="sm"
                                    disabled={isDisabled || purchasing === item.id}
                                    onClick={() => handlePurchase(item.id)}
                                    title={isDisabled ? `You need ${item.cost_in_gems} gems to purchase this item` : ''}
                                  >
                                    {purchasing === item.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      item.is_free ? 'Claim' : 'Buy'
                                    )}
                                  </Button>
                                  {isDisabled && (
                                    <span className="text-[10px] text-red-400 leading-tight">
                                      Need {item.cost_in_gems - (points?.gems || 0)} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Owned Badge - Only show for non-consumable items */}
                            {owned && !inventoryItem?.is_equipped && !isConsumable && (
                              <div className="text-[10px] text-green-400 text-center pt-1">
                                ✓ Owned
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* Show message if no items in a category, but still show default items */}
        {!loading && sortedGroups.length === 0 && (
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardContent className="p-8 text-center">
              <Sparkles className="w-12 h-12 text-[hsl(var(--primary))] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">More Items Coming Soon!</h3>
              <p className="text-muted-foreground">
                We're adding exciting rewards and power-ups. Keep earning gems to unlock exclusive items!
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

